import React, { useState, useEffect } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { useDispatch, useSelector } from 'react-redux';
import { toast } from 'react-toastify';
import {
  fetchInvoiceById,
  sendInvoice,
  addPayment,
  updateInvoice,
} from '../store/slices/invoicesSlice';
import { fetchProducts } from '../store/slices/productsSlice';
import { apiService } from '../services/api';
import InvoiceModal from '../components/InvoiceModal';
import PaymentModal from '../components/PaymentModal';
import './InvoiceDetail.css';

const STATUS_CONFIG = {
  draft:     { label: 'Draft',     bg: '#f3f4f6', color: '#6b7280' },
  sent:      { label: 'Sent',      bg: '#dbeafe', color: '#1d4ed8' },
  pending:   { label: 'Pending',   bg: '#ede9fe', color: '#6d28d9' },
  partial:   { label: 'Partial',   bg: '#fef3c7', color: '#b45309' },
  paid:      { label: 'Paid',      bg: '#d1fae5', color: '#065f46' },
  overdue:   { label: 'Overdue',   bg: '#fee2e2', color: '#991b1b' },
  cancelled: { label: 'Cancelled', bg: '#e5e7eb', color: '#374151' },
};

const InvoiceDetail = () => {
  const { id } = useParams();
  const navigate = useNavigate();
  const dispatch = useDispatch();

  const { currentInvoice: invoice, loading, error } = useSelector(state => state.invoices);
  const { items: rawProducts } = useSelector(state => state.products);

  const products = rawProducts.map(p => ({
    id: p._id,
    name: p.name,
    price: p.price ?? p.salePrice ?? 0,
    sku: p.retailerId || p.sku || '',
  }));

  console.log('InvoiceDetail render', { invoice });

  const [businessData, setBusinessData] = useState(null);
  const [pdfLoading, setPdfLoading] = useState(false);
  const [sendingInvoice, setSendingInvoice] = useState(false);
  const [isPaymentModalOpen, setIsPaymentModalOpen] = useState(false);
  const [isEditModalOpen, setIsEditModalOpen] = useState(false);

  useEffect(() => {
    dispatch(fetchInvoiceById(id))
      .unwrap()
      .catch(err => {
        console.error('Failed to fetch invoice:', err);
        toast.error('Failed to load invoice', { position: 'bottom-right' });
      });

    dispatch(fetchProducts({ limit: 200 }))
      .unwrap()
      .catch(err => console.error('Failed to fetch products:', err));

    apiService.auth.getBusinessData()
      .then(res => setBusinessData(res.data?.data || res.data || null))
      .catch(err => console.error('Failed to fetch business data:', err));
  }, [dispatch, id]);

  const formatDate = (dateString) => {
    if (!dateString) return 'N/A';
    const date = new Date(dateString);
    if (isNaN(date.getTime())) return 'N/A';
    return date.toLocaleDateString('en-US', { year: 'numeric', month: 'short', day: 'numeric' });
  };

  const formatCurrency = (value) => {
    return new Intl.NumberFormat('en-IN', {
      style: 'currency',
      currency: invoice?.currency || 'INR',
    }).format(value || 0);
  };

  const getStatusConfig = (status) =>
    STATUS_CONFIG[status?.toLowerCase()] || { label: status || 'Unknown', bg: '#f3f4f6', color: '#6b7280' };

  const handleDownloadPDF = async () => {
    setPdfLoading('download');
    try {
      const response = await apiService.invoices.downloadPDF(invoice._id);
      const blob = new Blob([response.data], { type: 'application/pdf' });
      const url = URL.createObjectURL(blob);
      const link = document.createElement('a');
      link.href = url;
      link.download = `Invoice-${invoice.invoiceNumber || invoice._id}.pdf`;
      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);
      URL.revokeObjectURL(url);
      toast.success('PDF downloaded', { position: 'bottom-right' });
    } catch (err) {
      console.error('PDF download error:', err);
      toast.error('Failed to generate PDF. Please try again.', { position: 'bottom-right' });
    } finally {
      setPdfLoading(false);
    }
  };

  const handlePreviewPDF = async () => {
    setPdfLoading('preview');
    try {
      const response = await apiService.invoices.previewPDF(invoice._id);
      const blob = new Blob([response.data], { type: 'application/pdf' });
      const url = URL.createObjectURL(blob);
      window.open(url, '_blank');
      setTimeout(() => URL.revokeObjectURL(url), 10000);
    } catch (err) {
      console.error('PDF preview error:', err);
      toast.error('Failed to preview PDF. Please try again.', { position: 'bottom-right' });
    } finally {
      setPdfLoading(false);
    }
  };

  const handleSend = async () => {
    const phoneNumberId = localStorage.getItem('phoneNumberId');
    const recipientPhone = invoice.billTo?.phone;
    if (!recipientPhone) {
      toast.error('Invoice has no recipient phone number. Please update the invoice.', { position: 'bottom-right' });
      return;
    }
    setSendingInvoice(true);
    try {
      await dispatch(sendInvoice({ invoiceId: invoice._id, phoneNumberId, recipientPhone })).unwrap();
      toast.success(`Invoice ${invoice.invoiceNumber} sent via WhatsApp`, { position: 'bottom-right' });
      dispatch(fetchInvoiceById(id));
    } catch (err) {
      toast.error(err?.message || err || 'Failed to send invoice', { position: 'bottom-right' });
    } finally {
      setSendingInvoice(false);
    }
  };

  const handleSavePayment = async (paymentData) => {
    try {
      await dispatch(addPayment({ invoiceId: invoice._id, paymentData })).unwrap();
      toast.success('Payment recorded successfully', { position: 'bottom-right' });
      setIsPaymentModalOpen(false);
      dispatch(fetchInvoiceById(id));
    } catch (err) {
      toast.error(err?.message || 'Failed to add payment', { position: 'bottom-right' });
    }
  };

  const handleSaveEdit = async (formData) => {
    try {
      await dispatch(updateInvoice({ id: invoice._id, data: formData })).unwrap();
      toast.success('Invoice updated successfully', { position: 'bottom-right' });
      setIsEditModalOpen(false);
      dispatch(fetchInvoiceById(id));
    } catch (err) {
      toast.error(err?.message || 'Failed to update invoice', { position: 'bottom-right' });
    }
  };

  /* Loading */
  if (loading && !invoice) {
    return (
      <div className="invoice-detail-container">
        <div className="loading-state">
          <div className="spinner"></div>
          <p>Loading invoice…</p>
        </div>
      </div>
    );
  }

  /* Error / Not found */
  if (!loading && (error || !invoice)) {
    return (
      <div className="invoice-detail-container">
        <div className="invoice-detail-header">
          <button className="btn-back" onClick={() => navigate('/invoices')}>← Back</button>
          <h1>Invoice Not Found</h1>
        </div>
        <div className="empty-state">
          {typeof error === 'string' ? error : error?.message || 'Invoice could not be loaded.'}
        </div>
      </div>
    );
  }

  if (!invoice) return null;

  const statusCfg = getStatusConfig(invoice.status);
  const balance = Math.max(0, (invoice.total || 0) - (invoice.paidAmount || 0));
  const paidPct = invoice.total > 0
    ? Math.min(100, Math.round(((invoice.paidAmount || 0) / invoice.total) * 100))
    : 0;

  return (
    <div className="invoice-detail-container">

      {/* Header */}
      <div className="invoice-detail-header">
        <button className="btn-back" onClick={() => navigate('/invoices')}>← Back</button>
        <h1>{invoice.invoiceNumber}</h1>
        <span className="invoice-status-badge" style={{ background: statusCfg.bg, color: statusCfg.color }}>
          {statusCfg.label}
        </span>

        <div className="header-actions">
          <button className="btn-header-action" onClick={handleDownloadPDF} disabled={pdfLoading !== false}>
            {pdfLoading === 'download' ? '⏳ Generating…' : '⬇ Download PDF'}
          </button>
          <button className="btn-header-action btn-secondary" onClick={handlePreviewPDF} disabled={pdfLoading !== false}>
            {pdfLoading === 'preview' ? '⏳ Loading…' : '👁 Preview PDF'}
          </button>
          <button className="btn-header-action btn-warning" onClick={() => setIsEditModalOpen(true)}>
            ✏ Edit
          </button>
          {invoice.status !== 'paid' && invoice.status !== 'cancelled' && (
            <button className="btn-header-action btn-purple" onClick={() => setIsPaymentModalOpen(true)}>
              💳 Add Payment
            </button>
          )}
          {invoice.status !== 'paid' && (
            <button className="btn-header-action btn-success" onClick={handleSend} disabled={sendingInvoice}>
              {sendingInvoice ? '⏳ Sending…' : '📤 Send'}
            </button>
          )}
        </div>
      </div>

      {/* Cards grid */}
      <div className="invoice-detail-grid">

        {/* Invoice Info */}
        <div className="detail-card">
          <h3>Invoice Details</h3>
          <div className="info-group">
            <div className="info-row">
              <span className="label">Invoice #</span>
              <span className="value">{invoice.invoiceNumber}</span>
            </div>
            <div className="info-row">
              <span className="label">Invoice Date</span>
              <span className="value">{formatDate(invoice.invoiceDate)}</span>
            </div>
            {invoice.dueDate && (
              <div className="info-row">
                <span className="label">Due Date</span>
                <span className="value">{formatDate(invoice.dueDate)}</span>
              </div>
            )}
            {invoice.paidAt && (
              <div className="info-row">
                <span className="label">Paid On</span>
                <span className="value" style={{ color: '#059669', fontWeight: 600 }}>{formatDate(invoice.paidAt)}</span>
              </div>
            )}
            <div className="info-row">
              <span className="label">Status</span>
              <span
                style={{
                  display: 'inline-block', padding: '2px 10px', borderRadius: '12px',
                  background: statusCfg.bg, color: statusCfg.color,
                  fontWeight: 700, fontSize: '12px', textTransform: 'uppercase',
                }}
              >
                {statusCfg.label}
              </span>
            </div>
            {invoice.currency && (
              <div className="info-row">
                <span className="label">Currency</span>
                <span className="value">{invoice.currency}</span>
              </div>
            )}
            <div className="info-row">
              <span className="label">Created</span>
              <span className="value">{formatDate(invoice.createdAt)}</span>
            </div>
          </div>
        </div>

        {/* Bill To */}
        <div className="detail-card">
          <h3>Bill To</h3>
          <div className="info-group">
            {invoice.billTo?.name && (
              <div className="info-row">
                <span className="label">Name</span>
                <span className="value">{invoice.billTo.name}</span>
              </div>
            )}
            {invoice.billTo?.phone && (
              <div className="info-row">
                <span className="label">Phone</span>
                <span className="value">{invoice.billTo.phone}</span>
              </div>
            )}
            {invoice.billTo?.email && (
              <div className="info-row">
                <span className="label">Email</span>
                <span className="value">{invoice.billTo.email}</span>
              </div>
            )}
            {invoice.billTo?.address && (
              <div className="info-row">
                <span className="label">Address</span>
                <span className="value">{invoice.billTo.address}</span>
              </div>
            )}
            {invoice.billTo?.gstin && (
              <div className="info-row">
                <span className="label">GSTIN</span>
                <span className="value">{invoice.billTo.gstin}</span>
              </div>
            )}
          </div>
        </div>

        {/* From (Merchant) */}
        {businessData && (
          <div className="detail-card">
            <h3>From</h3>
            <div className="info-group">
              {businessData.storeName && (
                <div className="info-row">
                  <span className="label">Business</span>
                  <span className="value">{businessData.storeName}</span>
                </div>
              )}
              {businessData.storeAddress && (
                <div className="info-row">
                  <span className="label">Address</span>
                  <span className="value">{businessData.storeAddress}</span>
                </div>
              )}
              {businessData.storePhone && (
                <div className="info-row">
                  <span className="label">Phone</span>
                  <span className="value">{businessData.storePhone}</span>
                </div>
              )}
              {businessData.storeEmail && (
                <div className="info-row">
                  <span className="label">Email</span>
                  <span className="value">{businessData.storeEmail}</span>
                </div>
              )}
              {businessData.gstNumber && (
                <div className="info-row">
                  <span className="label">GSTIN</span>
                  <span className="value">{businessData.gstNumber}</span>
                </div>
              )}
              {businessData.panNumber && (
                <div className="info-row">
                  <span className="label">PAN</span>
                  <span className="value">{businessData.panNumber}</span>
                </div>
              )}
            </div>
          </div>
        )}
      </div>

      {/* Line Items */}
      <div className="detail-card full-width">
        <h3>Items</h3>
        <table className="items-table">
          <thead>
            <tr>
              <th>Description</th>
              <th className="text-center">HSN/SAC</th>
              <th className="text-right">Qty</th>
              <th className="text-right">Unit Price</th>
              {(invoice.items || []).some(i => i.gstRate > 0) && (
                <th className="text-right">GST %</th>
              )}
              <th className="text-right">Total</th>
            </tr>
          </thead>
          <tbody>
            {(invoice.items || []).map((item, index) => (
              <tr key={item._id || index}>
                <td>{item.description || 'N/A'}</td>
                <td className="text-center">{item.hsn || 'N/A'}</td>
                <td className="text-right">{item.quantity}</td>
                <td className="text-right">{formatCurrency(item.unitPrice)}</td>
                {(invoice.items || []).some(i => i.gstRate > 0) && (
                  <td className="text-right">{item.gstRate > 0 ? `${item.gstRate}%` : 'N/A'}</td>
                )}
                <td className="text-right item-total">{formatCurrency(item.total)}</td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>

      {/* Payment Summary */}
      <div className="detail-card full-width">
        <h3>Payment Summary</h3>
        <div className="summary-rows">
          <div className="summary-row">
            <span>Subtotal</span>
            <span>{formatCurrency(invoice.subtotal)}</span>
          </div>
          {(invoice.discount || 0) > 0 && (
            <div className="summary-row">
              <span>Discount</span>
              <span>-{formatCurrency(invoice.discount)}</span>
            </div>
          )}
          {(invoice.cgst || 0) > 0 && (
            <div className="summary-row">
              <span>CGST ({invoice.cgst}%)</span>
              <span>{formatCurrency((invoice.subtotal * invoice.cgst) / 100)}</span>
            </div>
          )}
          {(invoice.sgst || 0) > 0 && (
            <div className="summary-row">
              <span>SGST ({invoice.sgst}%)</span>
              <span>{formatCurrency((invoice.subtotal * invoice.sgst) / 100)}</span>
            </div>
          )}
          {(invoice.igst || 0) > 0 && (
            <div className="summary-row">
              <span>IGST ({invoice.igst}%)</span>
              <span>{formatCurrency((invoice.subtotal * invoice.igst) / 100)}</span>
            </div>
          )}
          {(invoice.tax || 0) > 0 && !(invoice.cgst) && !(invoice.sgst) && !(invoice.igst) && (
            <div className="summary-row">
              <span>Tax</span>
              <span>{formatCurrency(invoice.tax)}</span>
            </div>
          )}
          <div className="summary-divider"></div>
          <div className="summary-row total">
            <span>Total</span>
            <span>{formatCurrency(invoice.total)}</span>
          </div>
          {(invoice.paidAmount || 0) > 0 && (
            <>
              <div className="summary-row">
                <span>Amount Paid</span>
                <span className="paid">{formatCurrency(invoice.paidAmount)}</span>
              </div>
              <div className="summary-row">
                <span>Balance Due</span>
                <span className={balance > 0 ? 'balance-due' : 'paid'}>{formatCurrency(balance)}</span>
              </div>
            </>
          )}
        </div>

        {/* Partial payment progress */}
        {invoice.status === 'partial' && (
          <div className="partial-progress">
            <div className="partial-progress-labels">
              <span>Paid: <strong>{formatCurrency(invoice.paidAmount)}</strong></span>
              <span className="pct-label">{paidPct}% paid</span>
              <span>Balance: <strong>{formatCurrency(balance)}</strong></span>
            </div>
            <div className="partial-progress-track">
              <div className="partial-progress-fill" style={{ width: `${paidPct}%` }} />
            </div>
          </div>
        )}
      </div>

      {/* Payment History */}
      {invoice.payments && invoice.payments.length > 0 && (
        <div className="detail-card full-width">
          <h3>Payment History</h3>
          <table className="items-table">
            <thead>
              <tr>
                <th>#</th>
                <th>Date</th>
                <th>Method</th>
                <th className="text-right">Amount</th>
                {invoice.payments.some(p => p.notes) && <th>Notes</th>}
              </tr>
            </thead>
            <tbody>
              {invoice.payments.map((payment, index) => (
                <tr key={payment._id || index}>
                  <td style={{ color: '#9ca3af' }}>{index + 1}</td>
                  <td>{formatDate(payment.date)}</td>
                  <td style={{ textTransform: 'capitalize' }}>{payment.method}</td>
                  <td className="text-right paid">{formatCurrency(payment.amount)}</td>
                  {invoice.payments.some(p => p.notes) && (
                    <td style={{ color: '#6b7280', fontSize: '13px' }}>{payment.notes || 'N/A'}</td>
                  )}
                </tr>
              ))}
            </tbody>
          </table>
          {invoice.payments.length > 1 && (
            <div style={{ textAlign: 'right', marginTop: '10px', fontSize: '13px', color: '#6b7280' }}>
              {invoice.payments.length} payments · Total paid:{' '}
              <strong className="paid">{formatCurrency(invoice.paidAmount)}</strong>
            </div>
          )}
        </div>
      )}

      {/* Notes */}
      {invoice.notes && (
        <div className="detail-card full-width">
          <h3>Notes</h3>
          <p className="notes-text">{invoice.notes}</p>
        </div>
      )}

      {/* Modals */}
      <InvoiceModal
        isOpen={isEditModalOpen}
        onClose={() => setIsEditModalOpen(false)}
        onSave={handleSaveEdit}
        invoice={invoice}
        products={products}
      />
      <PaymentModal
        isOpen={isPaymentModalOpen}
        onClose={() => setIsPaymentModalOpen(false)}
        onSave={handleSavePayment}
        invoice={invoice}
      />
    </div>
  );
};

export default InvoiceDetail;
